<script setup lang="ts">
const props = defineProps<{
    title: string,
    desc: string,
    currentProduct: string,
    icon: string,
    items: {
        title: string,
        desc: string,
        path: string,
        icon: string,
    }[]
}>();
const productStyles: Record<string, string> = {
    'nava-icon:products-internet-phone': 'border border-amber-50 shadow-warning-l',
    'nava-icon:products-sms': 'border border-info-50 shadow-info-l',
};
const productColors: Record<string, string> = {
    'nava-icon:products-internet-phone': 'text-amber-500',
    'nava-icon:products-sms': 'text-info-600',
    'nava-icon:products-nava-app': 'text-pink-500',
    'nava-icon:products-cloud-pbx': 'text-teal-500',
    'nava-icon:products-fax': 'text-purple-600',
    'nava-icon:products-crm': 'text-lime-500',
};

function handleClickOnLink(path: string) {
    navigateTo(path);
}
</script>

<template>
    <SectionsBase id="related-products" class="bg-gray-200">
        <div class="background container">
            <div class="w-full relative z-1">
                <ChipsDivider>محصولات‌ مرتبط</ChipsDivider>
                <h2>{{ props.title }}</h2>
                <p class="text-body1-semibold text-gray-400 text-center my-6">{{ props.desc }}</p>
                <div class="w-full mt-12">
                    <div class="w-full flex-center pb-25 lg:pb-20">
                        <div 
                            class="p-7 rounded-2xl bg-white flex items-center justify-center gap-3 border-4"
                            :class="[productColors[props.icon], productStyles[props.icon]]"
                        >
                            <Icon :name="props.icon" size="40" />
                            <span class="h3">{{ props.currentProduct }}</span>
                        </div>
                    </div>
                </div>
                <div class="w-full flex flex-col lg:flex-row justify-center items-center lg:items-start gap-10 lg:gap-2 px-1 lg:px-0 relative">
                    <DevicesMobile class="absolute w-80 -top-35 z-[-1]">
                        <svg viewBox="0 0 264 556" fill="none" xmlns="http://www.w3.org/2000/svg" class="w-full">
                            <path opacity="0.56" d="M137.277 16.0166L137.277 53.4325V56.6571V118.017" stroke="url(#paint0_linear_6172_26662)" stroke-width="1.5" stroke-dasharray="4 4"/>
                            <path opacity="0.56" d="M188.173 13.0166V83.9136C188.173 94.9593 197.127 103.914 208.173 103.914H244.633C254.574 103.914 262.633 111.972 262.633 121.914L262.633 261.017" stroke="url(#paint1_linear_6172_26662)" stroke-width="1.5" stroke-dasharray="4 4"/>
                            <path opacity="0.56" d="M94.8623 0.0166016L93.0555 80.7931C92.8124 91.6618 83.9319 100.346 73.0605 100.346H18.7494C8.75374 100.346 0.672469 108.49 0.749904 118.485L2.85349 390.017" stroke="url(#paint2_linear_6172_26662)" stroke-width="1.5" stroke-dasharray="4 4"/>
                            <path opacity="0.56" d="M262.633 341.017L262.633 419.517V426.282V555.017" stroke="url(#paint3_linear_6172_26662)" stroke-width="1.5" stroke-dasharray="4 4"/>
                            <defs>
                            <linearGradient id="paint0_linear_6172_26662" x1="151.585" y1="-40.2897" x2="126.232" y2="60.7038" gradientUnits="userSpaceOnUse">
                            <stop stop-color="white"/>
                            <stop offset="0.5" stop-color="#FFEEC1"/>
                            <stop offset="1" stop-color="#5A6A85"/>
                            </linearGradient>
                            <linearGradient id="paint1_linear_6172_26662" x1="173.855" y1="-110.405" x2="194.913" y2="125.046" gradientUnits="userSpaceOnUse">
                            <stop stop-color="#5A6A85"/>
                            <stop offset="0.5" stop-color="#B4B9C9"/>
                            <stop offset="1" stop-color="#5A6A85"/>
                            </linearGradient>
                            <linearGradient id="paint2_linear_6172_26662" x1="112.555" y1="-212.457" x2="62.4231" y2="189.874" gradientUnits="userSpaceOnUse">
                            <stop stop-color="white"/>
                            <stop offset="0.5" stop-color="#FFEEC1"/>
                            <stop offset="1" stop-color="#5A6A85"/>
                            </linearGradient>
                            <linearGradient id="paint3_linear_6172_26662" x1="248.325" y1="222.884" x2="341.195" y2="399.212" gradientUnits="userSpaceOnUse">
                            <stop stop-color="white"/>
                            <stop offset="0.5" stop-color="#FFEEC1"/>
                            <stop offset="1" stop-color="#5A6A85"/>
                            </linearGradient>
                            </defs>
                        </svg>
                    </DevicesMobile>
                    <DevicesDesktop class="absolute w-250 h-100 -top-50 z-[-1] ml-8">
                        <svg viewBox="0 0 855 167" fill="none" xmlns="http://www.w3.org/2000/svg" class="w-full h-full">
                            <path opacity="0.56" d="M364.75 0V48.2256C364.75 59.2713 355.796 68.2256 344.75 68.2256H18.75C8.80888 68.2256 0.75 76.2845 0.75 86.2256V167" stroke="url(#paint0_linear_6172_26657)" stroke-width="1.5" stroke-dasharray="4 4"/>
                            <path opacity="0.56" d="M364.75 0V48.2256C364.75 59.2713 355.796 68.2256 344.75 68.2256H253.75C243.809 68.2256 235.75 76.2845 235.75 86.2256V167" stroke="url(#paint1_linear_6172_26657)" stroke-width="1.5" stroke-dasharray="4 4"/>
                            <path opacity="0.56" d="M457.75 0V45.8025C457.75 56.8482 466.704 65.8025 477.75 65.8025H835.75C845.691 65.8025 853.75 73.8613 853.75 83.8025V164" stroke="url(#paint2_linear_6172_26657)" stroke-width="1.5" stroke-dasharray="4 4"/>
                            <path opacity="0.56" d="M457.75 0V45.8025C457.75 56.8482 466.704 65.8025 477.75 65.8025H568.75C578.691 65.8025 586.75 73.8613 586.75 83.8025V164" stroke="url(#paint3_linear_6172_26657)" stroke-width="1.5" stroke-dasharray="4 4"/>
                            <defs>
                            <linearGradient id="paint0_linear_6172_26657" x1="434.745" y1="-90.9823" x2="432.386" y2="83.9409" gradientUnits="userSpaceOnUse">
                            <stop stop-color="white"/>
                            <stop offset="0.5" stop-color="#FFEEC1"/>
                            <stop offset="1" stop-color="#5A6A85"/>
                            </linearGradient>
                            <linearGradient id="paint1_linear_6172_26657" x1="389.556" y1="-90.9823" x2="382.908" y2="83.7197" gradientUnits="userSpaceOnUse">
                            <stop stop-color="white"/>
                            <stop offset="0.5" stop-color="#FFEEC1"/>
                            <stop offset="1" stop-color="#5A6A85"/>
                            </linearGradient>
                            <linearGradient id="paint2_linear_6172_26657" x1="381.601" y1="-89.3479" x2="383.692" y2="82.4387" gradientUnits="userSpaceOnUse">
                            <stop stop-color="#5A6A85"/>
                            <stop offset="0.5" stop-color="#B4B9C9"/>
                            <stop offset="1" stop-color="#5A6A85"/>
                            </linearGradient>
                            <linearGradient id="paint3_linear_6172_26657" x1="432.944" y1="-89.3479" x2="439.356" y2="82.2246" gradientUnits="userSpaceOnUse">
                            <stop stop-color="#5A6A85"/>
                            <stop offset="0.5" stop-color="#B4B9C9"/>
                            <stop offset="1" stop-color="#5A6A85"/>
                            </linearGradient>
                            </defs>
                        </svg>
                    </DevicesDesktop>

                    <NuxtLink
                        v-for="item, index in props.items"
                        :key="item.title"
                        class="py-6 px-4 border-[6px] border-gray-300/20 rounded-2xl cursor-pointer bg-white h-min relative
                            hover:border-primary-50 hover:shadow-primary-m transition-all duration-300 select-none"
                        :class="[
                            index % 2 ? 'ml-10 lg:ml-0' : 'mr-10 lg:mr-0',
                            (index === 1 || index === 2) && 'lg:mt-11',
                        ]"
                        :to="item.path"
                    >
                        <div class="w-full flex items-center gap-5">
                            <Icon :name="item.icon" size="40" :class="productColors[item.icon]" />
                            <div>
                                <span class="text-body2-bold">{{ item.title }}</span>
                                <p class="text-caption text-gray-400">{{ item.desc }}</p>
                            </div>
                            <Icon 
                                name="solar:round-alt-arrow-left-outline"
                                size="20"
                                class="text-primary-600"
                            />
                        </div>
                    </NuxtLink>
                </div>
            </div>
        </div>
    </SectionsBase>
</template>

<style scoped>
@reference "~/assets/css/main.css";

.background {
    @apply
    bg-white relative rounded-[40px] pt-11 pb-20 border border-gray-200
}
.background::before {
    @apply
    content-[''] w-full h-full absolute top-0 left-0 bg-[url('/img/new/backgrounds/mesh-02.webp')] bg-no-repeat
    bg-size-[100%_100%] opacity-40 hidden! lg:block!
}
</style>